'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon } from '@/components/ui/icon';
import { NewTwitterIcon } from '@/components/ui/icon/brands';
import { InformationCircleIconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent, PanelHeader, PanelTitle } from '@/components/ui/panel';
import { cn } from '@/lib/ui/utils';
import { InfoTipModal, useRewardsTips } from './info-tip';
import { RankEmblem } from './rank-emblem';
interface Row {
  rank: number;
  handle: string | null;
  avatar: string | null;
  points: string;
  levelKey: string | null;
  /** Avatar seed — UserAvatar/OptimizedImage need it to paint the row's pfp. */
  userId: string | null;
}
interface LeaderboardPanelProps {
  board: {
    rows: Row[];
    me: {
      rank: number;
      points: string;
      levelKey: string | null;
    } | null;
  } | undefined;
  /** Viewer identity for the pinned "You" row — board.me carries no handle/avatar.
   *  `viewerUserId` is the avatar seed (account identity) so the row renders the
   *  same generated avatar as the profile card; without it UserAvatar can't paint
   *  the viewer's image. */
  viewerUserId?: string | null;
  viewerHandle?: string | null;
  viewerAvatar?: string | null;
  /** The level ladder (key → name + order) — resolves each row's emblem into a
   *  "<name> · pos/total" hover tooltip. */
  ladder?: readonly {
    key: string;
    name: string;
    order: number;
  }[] | undefined;
  /** Opens the full level-ladder modal — wired to the emblem tooltip's info icon. */
  onOpenLadder?: (() => void) | undefined;
  /** Replaces the default "Points leaderboard" title row — lets the dashboard
   *  host the Leaderboard/Milestones segmented switch + info icon in the header. */
  headerSlot?: React.ReactNode;
  className?: string;
}

/** A row's level resolved against the ladder, for the emblem hover tooltip. */
interface LevelInfo {
  name: string;
  pos: number;
  total: number;
}
export function LeaderboardPanel(t0) {
  const $ = _c(40);
  const {
    board,
    viewerUserId,
    viewerHandle,
    viewerAvatar,
    ladder,
    onOpenLadder,
    headerSlot,
    className
  } = t0;
  const tips = useRewardsTips();
  const t = useTranslations("rewards");
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== board?.me || $[1] !== board?.rows || $[2] !== className || $[3] !== headerSlot || $[4] !== ladder || $[5] !== onOpenLadder || $[6] !== t || $[7] !== tips || $[8] !== viewerAvatar || $[9] !== viewerHandle || $[10] !== viewerUserId) {
    const sortedLadder = (ladder ?? []).toSorted(_temp);
    const levelByKey = new Map(sortedLadder.map((r, i) => [r.key, {
      name: r.name,
      pos: i + 1,
      total: sortedLadder.length
    }]));
    const levelFor = key => key !== null ? levelByKey.get(key) ?? null : null;
    const me = board?.me ?? null;
    const rows = board?.rows ?? [];
    const viewerInList = me !== null && rows.some(r_0 => r_0.rank === me.rank);
    T1 = Panel;
    t5 = "leaderboard-panel";
    if ($[20] !== className) {
      t6 = cn("gap-4 lg:overflow-hidden", className);
      $[20] = className;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    let t8;
    if ($[22] !== headerSlot || $[23] !== t || $[24] !== tips) {
      t8 = headerSlot ?? <PanelTitle className="flex items-center gap-1.5">{t("leaderboard.title")}<InfoTipModal tip={tips.board} /></PanelTitle>;
      $[22] = headerSlot;
      $[23] = t;
      $[24] = tips;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    if ($[26] !== t8) {
      t7 = <PanelHeader>{t8}</PanelHeader>;
      $[26] = t8;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    T0 = PanelContent;
    t1 = "flex flex-col gap-1 lg:min-h-0 lg:flex-1 lg:overflow-y-auto";
    t2 = me === null && rows.length === 0 ? <p className="px-2 py-6 text-center text-caption text-foreground-tertiary">{t("leaderboard.empty")}</p> : null;
    t3 = rows.map(r_1 => {
      const isViewer = me !== null && r_1.rank === me.rank;
      return <BoardRow key={r_1.rank} rank={r_1.rank} userId={isViewer ? viewerUserId ?? r_1.userId : r_1.userId} handle={isViewer ? viewerHandle ?? r_1.handle : r_1.handle} avatar={isViewer ? viewerAvatar ?? r_1.avatar : r_1.avatar} points={r_1.points} levelKey={r_1.levelKey} level={levelFor(r_1.levelKey)} onOpenLadder={onOpenLadder} you={isViewer} />;
    });
    t4 = me !== null && !viewerInList ? <BoardRow rank={me.rank} userId={viewerUserId ?? null} handle={viewerHandle ?? null} avatar={viewerAvatar ?? null} points={me.points} levelKey={me.levelKey} level={levelFor(me.levelKey)} onOpenLadder={onOpenLadder} you={true} pinned={true} /> : null;
    $[0] = board?.me;
    $[1] = board?.rows;
    $[2] = className;
    $[3] = headerSlot;
    $[4] = ladder;
    $[5] = onOpenLadder;
    $[6] = t;
    $[7] = tips;
    $[8] = viewerAvatar;
    $[9] = viewerHandle;
    $[10] = viewerUserId;
    $[11] = T0;
    $[12] = T1;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    T0 = $[11];
    T1 = $[12];
    t1 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
  }
  let t8;
  if ($[28] !== T0 || $[29] !== t1 || $[30] !== t2 || $[31] !== t3 || $[32] !== t4) {
    t8 = <T0 className={t1}>{t2}{t3}{t4}</T0>;
    $[28] = T0;
    $[29] = t1;
    $[30] = t2;
    $[31] = t3;
    $[32] = t4;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== T1 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8) {
    t9 = <T1 data-testid={t5} className={t6}>{t7}{t8}</T1>;
    $[34] = T1;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}

/** One leaderboard row. `you` highlights the viewer's row (distinct surface);
 *  `pinned` additionally sticks it to the bottom of the scroll area when the
 *  viewer ranks below the visible page so their standing stays visible. The top
 *  3 show the gold/silver/bronze ribbon in place of the rank number. */
function _temp(a, b) {
  return a.order - b.order;
}
function BoardRow(t0) {
  const $ = _c(37);
  const {
    rank,
    userId: t1,
    handle,
    avatar,
    points,
    levelKey,
    level,
    onOpenLadder,
    you: t2,
    pinned: t3
  } = t0;
  const userId = t1 === undefined ? null : t1;
  const you = t2 === undefined ? false : t2;
  const pinned = t3 === undefined ? false : t3;
  const t = useTranslations("rewards");
  const displayLabel = handle;
  let t4;
  if ($[0] !== t || $[1] !== you) {
    t4 = you ? t("leaderboard.youPrefix") : "";
    $[0] = t;
    $[1] = you;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const youPrefix = t4;
  const t5 = you ? "leaderboard-you-row" : `leaderboard-row-${String(rank)}`;
  const t6 = you && "bg-background-tertiary";
  const t7 = pinned && "z-10 lg:sticky lg:bottom-0";
  let t8;
  if ($[3] !== t6 || $[4] !== t7) {
    t8 = cn("flex items-center gap-3 rounded-panel-row px-2 py-2", t6, t7);
    $[3] = t6;
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const t9 = rank <= 3 ? "font-medium text-product-ranking-top" : "text-foreground-tertiary";
  let t10;
  if ($[6] !== t9) {
    t10 = cn("flex w-6 shrink-0 items-center justify-center text-caption", t9);
    $[6] = t9;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== rank || $[9] !== t) {
    t11 = rank <= 3 ? <Image src={`/images/social/podium/rank-${String(rank)}.png`} alt={t("leaderboard.rankAlt", {
      rank
    })} width={22} height={22} className="object-contain" /> : rank;
    $[8] = rank;
    $[9] = t;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== t10 || $[12] !== t11) {
    t12 = <span className={t10}>{t11}</span>;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== avatar || $[15] !== displayLabel || $[16] !== userId) {
    t13 = <UserAvatar userId={userId} displayName={displayLabel} avatarUrl={avatar} size="sm" nsfw={false} rasterise={false} />;
    $[14] = avatar;
    $[15] = displayLabel;
    $[16] = userId;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== level || $[19] !== levelKey || $[20] !== onOpenLadder || $[21] !== t) {
    t14 = levelKey ? <EmblemTip levelKey={levelKey} level={level} label={t("leaderboard.levelLabel", {
      level: levelKey.replace(/_/g, " ")
    })} onOpenLadder={onOpenLadder} /> : null;
    $[18] = level;
    $[19] = levelKey;
    $[20] = onOpenLadder;
    $[21] = t;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== handle || $[24] !== t || $[25] !== youPrefix) {
    t15 = handle ? <a href={`https://x.com/${handle}`} target="_blank" rel="noopener noreferrer" aria-label={t("leaderboard.openOnX", {
      handle
    })} className="group flex min-w-0 flex-1 items-center gap-1 text-caption text-foreground-secondary hover:text-foreground-primary"><span className="min-w-0 truncate">{youPrefix}@{handle}</span><Icon icon={NewTwitterIcon} className="size-3 shrink-0 text-foreground-tertiary group-hover:text-foreground-secondary" /></a> : <span className="min-w-0 flex-1 truncate text-caption text-foreground-secondary">{youPrefix}@anon</span>;
    $[23] = handle;
    $[24] = t;
    $[25] = youPrefix;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== points) {
    t16 = <span className="text-caption font-medium text-foreground-primary">{points}</span>;
    $[27] = points;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== t15 || $[33] !== t16 || $[34] !== t5 || $[35] !== t8) {
    t17 = <div data-testid={t5} className={t8}>{t12}{t13}{t14}{t15}{t16}</div>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t5;
    $[35] = t8;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
}

/**
 * Rank emblem + its level tooltip — a black pill with white text reading
 * "<name>  pos/total  ⓘ", the ⓘ opening the full level-ladder modal. Pointer
 * devices reveal it on hover (Radix); touch has no hover, so a TAP reveals it
 * and it auto-hides after 2s.
 */
function EmblemTip(t0) {
  const $ = _c(31);
  const {
    levelKey,
    level,
    label,
    onOpenLadder
  } = t0;
  const t = useTranslations("rewards");
  const [open, setOpen] = useState(false);
  const hideTimer = useRef(null);
  const touchHold = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (hideTimer.current !== null) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const clearHide = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      if (hideTimer.current !== null) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = next => {
      if (touchHold.current && !next) {
        return;
      }
      setOpen(next);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleOpenChange = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => {
      if (e.pointerType !== "touch") {
        return;
      }
      touchHold.current = true;
      setOpen(true);
      clearHide();
      hideTimer.current = setTimeout(() => {
        touchHold.current = false;
        setOpen(false);
      }, 2000);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const revealForTouch = t5;
  let t6;
  if ($[5] !== onOpenLadder) {
    t6 = () => {
      clearHide();
      touchHold.current = false;
      setOpen(false);
      onOpenLadder?.();
    };
    $[5] = onOpenLadder;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const openLadder = t6;
  let t7;
  if ($[7] !== level?.name || $[8] !== levelKey) {
    t7 = level?.name ?? levelKey.replace(/_/g, " ");
    $[7] = level?.name;
    $[8] = levelKey;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <span>{t7}</span>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== level) {
    t9 = level ? <span className="text-micro text-white/70">{level.pos}/{level.total}</span> : null;
    $[12] = level;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== onOpenLadder || $[15] !== openLadder || $[16] !== t) {
    t10 = onOpenLadder ? <button type="button" aria-label={t("ladder.title")} onClick={openLadder} className="inline-flex shrink-0 cursor-pointer text-white/70 transition-colors hover:text-white"><Icon icon={InformationCircleIconSolid} className="size-3" /></button> : null;
    $[14] = onOpenLadder;
    $[15] = openLadder;
    $[16] = t;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8 || $[20] !== t9) {
    t11 = <span className="flex items-center gap-1.5">{t8}{t9}{t10}</span>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== levelKey) {
    t12 = <RankEmblem rankKey={levelKey} size={20} />;
    $[22] = levelKey;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== label || $[25] !== t12) {
    t13 = <button type="button" aria-label={label} onPointerDown={revealForTouch} className="inline-flex shrink-0 cursor-pointer items-center">{t12}</button>;
    $[24] = label;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== open || $[28] !== t11 || $[29] !== t13) {
    t14 = <AppTooltip open={open} onOpenChange={handleOpenChange} delayDuration={200} showArrow={false} contentClassName="bg-surface-scrim-strong text-white" content={t11}>{t13}</AppTooltip>;
    $[27] = open;
    $[28] = t11;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
}